// Arrival-form party state: ONE array of guest rows is the truth; the
// adults/children counts are DERIVED projections of it (never stored
// separately, so the two can never diverge — OBR-000064: VRBO sent no
// modification email when 1 guest became 10 adults + 6 kids, and Master +
// the Cleaner Schedule never learned it).
//
// Band rule (mirrored server-side in intake.helpers.map_arrival_updates):
// age "18+" → adult; anything else (1–17, or not-yet-picked "") → child.
// A child row created by the stepper starts with a blank age so the guest
// picks the real one — it still counts as a child immediately, and submit
// validation (validateArrival) requires the age before the form goes out.
import { MAX_GUESTS } from "../config.js";

export const ADULT = "adult";
export const CHILD = "child";
export const MIN_ADULTS = 1;

// Stages at which the guest list + counts are frozen (arrival is imminent or
// past — the cleaners have already prepared). Flights stay editable. Mirrors
// the server-side freeze in intake.processor.process_arrival.
export const FROZEN_STAGES = new Set(["sesamed", "wrapped", "closed", "lost", "cancelled"]);

export function bandOf(g) {
  return g && g.age === "18+" ? ADULT : CHILD;
}

export function isFrozenStage(stage) {
  return FROZEN_STAGES.has(String(stage || "").toLowerCase());
}

/** Derived counts — the ONLY way counts are ever known. */
export function countByBand(guests) {
  const list = guests || [];
  const adults = list.filter((g) => bandOf(g) === ADULT).length;
  return { adults, children: list.length - adults };
}

/** Blank rows from a counts snapshot (arrival token or server payload). */
export function skeletonGuests(adults, children) {
  const ad = Math.max(MIN_ADULTS, parseInt(adults, 10) || MIN_ADULTS);
  const ch = Math.max(0, parseInt(children, 10) || 0);
  const list = [];
  for (let i = 0; i < ad; i++) list.push({ name: "", age: "18+" });
  for (let i = 0; i < ch; i++) list.push({ name: "", age: "" });
  return list.slice(0, MAX_GUESTS);
}

/**
 * Change a band's count by `delta` — the count steppers' view on the row
 * array. Increasing appends blank rows of that band (capped at MAX_GUESTS
 * total); decreasing removes rows of that band, EMPTY rows first (last-to-
 * first), then filled ones from the end (the row visibly disappears — the
 * data loss is explicit in the UI, no modal). Returns a NEW array; the input
 * is never mutated.
 */
export function changeBandCount(guests, band, delta) {
  const gs = guests || [];
  if (delta > 0) {
    const room = MAX_GUESTS - gs.length;
    const add = Math.min(delta, room);
    if (add <= 0) return gs.slice();
    const blank = band === ADULT ? { name: "", age: "18+" } : { name: "", age: "" };
    return [...gs, ...Array.from({ length: add }, () => ({ ...blank }))];
  }
  let k = Math.min(-delta, gs.filter((g) => bandOf(g) === band).length);
  const drop = new Set();
  for (let i = gs.length - 1; i >= 0 && drop.size < k; i--) {
    if (bandOf(gs[i]) === band && !String(gs[i].name || "").trim()) drop.add(i);
  }
  for (let i = gs.length - 1; i >= 0 && drop.size < k; i--) {
    if (bandOf(gs[i]) === band && String(gs[i].name || "").trim()) drop.add(i);
  }
  return gs.filter((_, i) => !drop.has(i));
}

/**
 * May row `i` be removed directly? At least one row must remain, and the
 * party must keep at least MIN_ADULTS adults — the same invariant the
 * steppers enforce, so neither view can break it.
 */
export function canRemoveRow(guests, i) {
  const gs = guests || [];
  if (gs.length <= 1) return false;
  if (bandOf(gs[i]) === ADULT && countByBand(gs).adults <= MIN_ADULTS) return false;
  return true;
}
